/**
 * SpeedmasterCore.PDP.Condition.View.js
 *
 * Condition card on the PDP for non-Brand-New items:
 *
 *  - Brand New / empty condition → renders nothing (hidden).
 *  - Refurbished / Outlet / Used / Parts → expandable card with
 *    color-coded badge, detail grid, inspector notes.
 *  - If no detail fields exist (cosmetic, components, packaging, notes)
 *    the card renders as a static bar — no toggle, no expandable.
 *
 * Badge color classes map to the outlet page condition tiers:
 *   Open Box → green, Refurbished → yellow, Used → orange,
 *   For Parts or Repair → red.
 *
 * Mounted via pdp.addChildViews() in SpeedmasterCore.Shopping.js
 * at the StockDescription slot.
 *
 * This view ALSO renders the Promo/Clearance reseller disclaimer, as a sibling
 * of the condition card. It is gated independently (on the badge, not on
 * showConditionCard) so a badged item with no condition fields still shows it.
 * Driven by custitem_ef_badges + speedmaster.PromoDisclaimerEnabled.
 */
define('SpeedmasterCore.PDP.Condition.View', [
    'speedmastercore_pdp_condition.tpl',
    'SC.Configuration',
    'Backbone'
], function(
    speedmastercore_pdp_condition_tpl,
    Configuration,
    Backbone
) {
    'use strict';

    /**
     * Check if a string has any non-whitespace content.
     */
    function hasValue(val) {
        return (val || '').replace(/^\s+|\s+$/g, '') !== '';
    }

    /**
     * Brand New items (and empty condition) are hidden entirely.
     */
    function isBrandNew(condition) {
        var lower = (condition || '').replace(/^\s+|\s+$/g, '').toLowerCase();
        return lower === '' || lower === 'brand new' || lower === 'new';
    }

    /**
     * Map condition value to a CSS badge class for color-coding.
     * Matches the outlet page condition tiers.
     */
    function getBadgeClass(condition) {
        var lower = (condition || '').replace(/^\s+|\s+$/g, '').toLowerCase();
        if (lower === 'open box')      { return 'openbox'; }
        if (lower === 'refurbished')   { return 'refurbished'; }
        if (lower === 'used')          { return 'used'; }
        if (lower.indexOf('parts') !== -1 || lower.indexOf('repair') !== -1) { return 'parts'; }
        return 'default';
    }

    /**
     * custitem_ef_badges arrives as the literal string "&nbsp;" when no badge
     * is set — NOT an empty string, so hasValue() would wrongly report it as
     * present. Strip the entity, the raw nbsp char and surrounding whitespace
     * so an unbadged item normalizes to ''.
     */
    function normalizeBadge(value) {
        return String(value || '')
            .replace(/&nbsp;?/gi, ' ')
            .replace(/\u00a0/g, ' ')
            .replace(/^\s+|\s+$/g, '');
    }

    /**
     * Allowlist, never blocklist: only the badges named in
     * speedmaster.PromoDisclaimerBadges show the disclaimer, so other badge
     * values ("Outlet", "New") can never trigger it.
     */
    function isDisclaimerBadge(badge) {
        if (!badge) { return false; }

        var allowed = (Configuration.get('speedmaster.PromoDisclaimerBadges') || '').split(',');
        var needle = badge.toLowerCase();
        var i;

        for (i = 0; i < allowed.length; i++) {
            if (normalizeBadge(allowed[i]).toLowerCase() === needle) { return true; }
        }
        return false;
    }

    return Backbone.View.extend({
        template: speedmastercore_pdp_condition_tpl,

        initialize: function(options) {
            this.pdp = options.pdp;
            this.itemInfo = this.pdp.getItemInfo();
        },

        getContext: function() {
            var item = this.itemInfo.item;
            var condition   = item.custitem_condition || '';
            var cosmetic    = item.custitem_cosmetic_status || '';
            var packaging   = item.custitem_packaging_status || '';
            var components  = item.custitem_components_status || '';
            var notes       = item.custitem_condition_notes || '';

            /* ── Promo / Clearance reseller disclaimer ─────────────────────
               Gated on the BADGE only — never on showConditionCard — so a
               badged item with no condition fields still renders it. Never on
               price, so a logged-in reseller's tier pricing can't trigger it. */
            var badge = normalizeBadge(item.custitem_ef_badges);
            var showPromoDisclaimer = !!Configuration.get('speedmaster.PromoDisclaimerEnabled')
                && isDisclaimerBadge(badge);

            var sku = item.itemid || '';
            var jegsTemplate = Configuration.get('speedmaster.JegsUrlTemplate') || '';
            var jegsUrl = (showPromoDisclaimer && sku && jegsTemplate)
                ? jegsTemplate.replace('{sku}', encodeURIComponent(sku))
                : '';

            return {
                // Card visibility — hidden for Brand New / empty
                showConditionCard: hasValue(condition) && !isBrandNew(condition),

                // Expandable vs static — only expandable when detail fields exist
                hasDetailFields: hasValue(cosmetic) || hasValue(packaging)
                    || hasValue(components) || hasValue(notes),

                // Badge color class
                badgeClass: getBadgeClass(condition),

                // Display values
                condition: condition,
                cosmetic_status: cosmetic,
                packaging_status: packaging,
                component_status: components,
                condition_notes: notes,

                // Promo / Clearance reseller disclaimer
                showPromoDisclaimer: showPromoDisclaimer,
                badgeLabel: badge,
                jegsUrl: jegsUrl
            };
        }
    });
});
